/**
 * Google Analytics 4 (gtag.js).
 *
 * The tag itself is installed in `index.html` with `send_page_view: false` and
 * is skipped entirely on localhost, so GA is a no-op in development. Because a
 * single `index.html` is served for every SPA route, page views are reported
 * here on each client-side navigation instead of once at load.
 *
 * Like the rest of the tracking in this app, analytics is best-effort: it never
 * throws and never blocks the UI. Crawlers and automated browsers send nothing
 * (see `isLikelyBot`); GA4's built-in filter only drops traffic from bots on
 * its known list, not headless or WebDriver-driven browsers.
 *
 * Besides page views this module owns every GA4 event the site sends — one
 * typed function per event below, so names and parameters live in one place.
 * Parameter names follow GA4's recommended events where one exists (`share`,
 * `search`, `select_promotion`).
 *
 * Page addresses, titles and search terms are scrubbed before they leave (see
 * "Personal data"): e-mail addresses, phone numbers and reset tokens never
 * reach the property. The privacy policy's "Analytics on this website" section
 * describes what is sent — keep it in step when adding an event or parameter.
 */
import type { PageTemplate } from '@/lib/pageTemplate';
import { isLikelyBot } from '@/utils/bot';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * Wait briefly before reporting, so the lazy page chunk has mounted and
 * the page has rendered the route's real `<title>` (GA reads `page_title` at
 * send time). Navigating away sooner flushes the pending view immediately, so
 * nothing is lost — only the title may be less specific.
 */
const PAGE_VIEW_DELAY_MS = 600;

/**
 * Production builds, in a browser, used by a person. The gtag.js tag is not
 * installed on localhost either (index.html), so development never reaches the
 * property. Exported for `@/lib/webVitals`, which checks it before it even
 * downloads its measuring library.
 */
export function canReport(): boolean {
  return typeof window !== 'undefined' && import.meta.env.PROD && !isLikelyBot();
}

function send(path: string): void {
  try {
    const { href, pathname, search } = window.location;
    // The address bar has moved on when a pending view is flushed by a
    // navigation, or when /search's query changes again while its settled view
    // waits (usePageViews): describe the page being reported, not the new one.
    const location = `${pathname}${search}` === path ? href : new URL(path, href).href;
    window.gtag?.('event', 'page_view', {
      page_path: redactUrl(path),
      page_location: redactUrl(location),
      page_title: redactPersonalData(document.title),
    });
  } catch {
    /* analytics must never throw — this also runs inside an effect cleanup */
  }
}

/**
 * Report one `page_view` for `path`. Returns a cleanup that flushes the view if
 * it hasn't been sent yet — designed to be returned straight from a `useEffect`
 * keyed on the location.
 */
export function trackPageView(path: string): () => void {
  if (!canReport()) return () => {};

  let timer: number | undefined = window.setTimeout(() => {
    timer = undefined;
    send(path);
  }, PAGE_VIEW_DELAY_MS);

  return () => {
    if (timer === undefined) return; // already sent
    window.clearTimeout(timer);
    send(path);
  };
}

/**
 * How long /search's query string must stay unchanged before it counts as a
 * search. The search box rewrites ?query= every 300 ms while the visitor types,
 * so without this "n", "ni", "nik"… would each count. The `search` event
 * (SearchPage) and /search's page_view (usePageViews) both wait this long.
 */
export const SEARCH_SETTLE_MS = 1000;

// ─── Personal data ──────────────────────────────────────────────────────────
//
// Google Analytics must not receive anything that identifies a person, and
// visitors do type e-mail addresses and phone numbers into the search box —
// which then sits in ?query= and in the page title. Everything this module
// hands to gtag.js goes through the scrubbers below.

/** Longest search term reported to GA4, after redaction. */
export const MAX_SEARCH_TERM_LENGTH = 50;

/**
 * Anything shaped like an e-mail address: text on both sides of an "@", up to
 * whitespace, quotes, brackets or punctuation that cannot sit in an address
 * (the /search title wraps the term in quotes).
 */
const EMAIL_RE = /[^\s@"'`<>()[\]{},;:]+@[^\s@"'`<>()[\]{},;:]+/g;

/**
 * Seven or more digits, optionally led by "+" or "(" and split by spaces,
 * dots, dashes, slashes or brackets: phone numbers (0300-1234567,
 * +92 300 1234567, (042) 3576 1234), CNICs, card and account numbers. Prices,
 * years and sale dates ("12.12") are shorter; the odd longer run that is not
 * personal is redacted too — the safe side to err on.
 */
const DIGIT_RUN_RE = /[+(]?\d(?:[\s().\/-]{0,3}\d){6,}/g;

/** E-mail addresses become `[email]`; phone-like digit runs become `[phone]`. */
export function redactPersonalData(text: string): string {
  return text.replace(EMAIL_RE, '[email]').replace(DIGIT_RUN_RE, '[phone]');
}

/**
 * A search term as GA4 may receive it: whitespace collapsed, personal data
 * redacted, and cut to MAX_SEARCH_TERM_LENGTH characters (a long paste is more
 * likely to be an address or a message than a product name).
 */
export function redactSearchTerm(term: string): string {
  const redacted = redactPersonalData(term.trim().replace(/\s+/g, ' '));
  if (redacted.length <= MAX_SEARCH_TERM_LENGTH) return redacted;
  // Never leave half a placeholder ("[pho") at the cut.
  return redacted.slice(0, MAX_SEARCH_TERM_LENGTH).replace(/\[[a-z]*$/, '').trimEnd();
}

/**
 * Secret path segments: the e-mail confirmation and password-reset tokens
 * (`/user/confirm/:token`, `/reset-password/:token`,
 * `/user/reset-password/:token`). A reset link works for whoever holds it.
 */
const TOKEN_PATH_RE =
  /^((?:[a-z][a-z\d+.-]*:\/\/[^/?#]*)?\/(?:user\/confirm|(?:user\/)?reset-password)\/)[^/?#]+/i;

/** Query parameters scrubbed by name, whatever their value looks like. */
const PARAM_REDACTIONS: Record<string, (value: string) => string> = {
  // The /search box.
  query: redactSearchTerm,
  // /verify-otp: the e-mail address, phone number or username being verified.
  identifier: (value) => (value ? '[redacted]' : value),
};

function decodeParam(raw: string): string {
  try {
    return decodeURIComponent(raw.replace(/\+/g, ' '));
  } catch {
    return raw;
  }
}

/** Encoded like URLSearchParams does, but the placeholders stay readable. */
function encodeParam(value: string): string {
  return encodeURIComponent(value)
    .replace(/%20/g, '+')
    .replace(/%5B/g, '[')
    .replace(/%5D/g, ']');
}

function redactParam(pair: string): string {
  const eq = pair.indexOf('=');
  if (eq === -1) return pair;
  const rawKey = pair.slice(0, eq);
  const value = decodeParam(pair.slice(eq + 1));
  const redact = PARAM_REDACTIONS[decodeParam(rawKey).toLowerCase()];
  const safe = redact ? redact(value) : value.replace(EMAIL_RE, '[email]');
  return safe === value ? pair : `${rawKey}=${encodeParam(safe)}`;
}

/**
 * `url` (absolute, or a path with its query) as GA4 may receive it: reset and
 * confirmation tokens become `[token]`, `?query=` is scrubbed like a search
 * term, `?identifier=` becomes `[redacted]`, and e-mail addresses in any other
 * parameter become `[email]`. Everything else — campaign parameters such as
 * `utm_*` and `gclid` included — passes through byte for byte.
 */
export function redactUrl(url: string): string {
  const hashAt = url.indexOf('#');
  const hash = hashAt === -1 ? '' : url.slice(hashAt);
  const beforeHash = hashAt === -1 ? url : url.slice(0, hashAt);
  const queryAt = beforeHash.indexOf('?');
  const base = (queryAt === -1 ? beforeHash : beforeHash.slice(0, queryAt)).replace(
    TOKEN_PATH_RE,
    '$1[token]',
  );
  if (queryAt === -1) return `${base}${hash}`;
  const query = beforeHash.slice(queryAt + 1).split('&').map(redactParam).join('&');
  return `${base}?${query}${hash}`;
}

/**
 * gtag.js adds the browser's own address and title to every event, and on
 * /search both carry what was typed. When scrubbing changes either one, the
 * scrubbed value is sent with the event in its place.
 */
function scrubbedPageContext(): Record<string, string> {
  const context: Record<string, string> = {};
  const location = redactUrl(window.location.href);
  if (location !== window.location.href) context.page_location = location;
  const title = redactPersonalData(document.title);
  if (title !== document.title) context.page_title = title;
  return context;
}

// ─── Events ─────────────────────────────────────────────────────────────────

/** What an event is about: the entity kinds the site has pages for. */
export type ContentType = 'promotion' | 'brand' | 'shop' | 'store';

type EventParams = Record<string, string | number | boolean | null | undefined>;

/**
 * GA4 limits event parameter values to 100 characters (standard property).
 * Long names and URLs are cut here, predictably, rather than by GA.
 */
export const MAX_PARAM_LENGTH = 100;

/** Send one GA4 event. Empty parameters are left out rather than sent blank. */
function sendEvent(name: string, params: EventParams = {}): void {
  if (!canReport()) return;
  const payload: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    payload[key] = typeof value === 'string' ? value.slice(0, MAX_PARAM_LENGTH) : value;
  }
  try {
    Object.assign(payload, scrubbedPageContext());
    window.gtag?.('event', name, payload);
  } catch {
    /* analytics must never throw */
  }
}

/**
 * How a share left the page: the native share sheet (the target app is not
 * reported by the Web Share API) or the clipboard fallback.
 */
export type ShareMethod = 'web_share' | 'copy_link';

/**
 * GA4 recommended `share`. Sent only for a share that happened: a dismissed
 * share sheet is not a share. The shared URL itself carries no UTM parameters
 * (see `shareUrl`).
 */
export function trackShare({
  method,
  contentType,
  itemId,
}: {
  method: ShareMethod;
  contentType: ContentType;
  itemId: string;
}): void {
  sendEvent('share', { method, content_type: contentType, item_id: itemId });
}

/**
 * GA4 recommended `select_promotion`: a promotion was opened from a card or
 * the home carousel. `creativeSlot` names the placement (`promotion_card`,
 * `hero_carousel_2`…); the page it happened on comes with every event anyway.
 */
export function trackSelectPromotion({
  promotionId,
  promotionName,
  creativeSlot,
}: {
  promotionId: string;
  promotionName?: string | null;
  creativeSlot: string;
}): void {
  sendEvent('select_promotion', {
    promotion_id: promotionId,
    promotion_name: promotionName,
    creative_slot: creativeSlot,
  });
}

function hostnameOf(url: string): string | undefined {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return undefined;
  }
}

/**
 * The entity's own website was opened from its detail page (the outbound link
 * a brand cares most about). `link_url`/`link_domain` match the parameter
 * names of GA4's automatic outbound `click`, so both can share reports.
 */
export function trackBrandWebsiteClick({
  contentType,
  itemId,
  url,
}: {
  contentType: ContentType;
  itemId: string;
  url: string;
}): void {
  sendEvent('click_brand_website', {
    content_type: contentType,
    item_id: itemId,
    link_url: url,
    link_domain: hostnameOf(url),
  });
}

/** Directions / the maps place page were opened for a store or shop. */
export function trackGetDirections({
  contentType,
  itemId,
}: {
  contentType: 'store' | 'shop';
  itemId?: string | null;
}): void {
  sendEvent('get_directions', { content_type: contentType, item_id: itemId });
}

/**
 * A signed-in visitor started following a brand, shop or store. Sent once the
 * server has accepted it; unfollowing sends nothing.
 */
export function trackFollow({ contentType, itemId }: { contentType: ContentType; itemId: string }): void {
  sendEvent('follow', { content_type: contentType, item_id: itemId });
}

/**
 * A signed-in visitor added a brand, shop or store to their favorites. Sent
 * once the server has accepted it; removing a favorite sends nothing.
 */
export function trackFavorite({
  contentType,
  itemId,
}: {
  contentType: ContentType;
  itemId: string;
}): void {
  sendEvent('favorite', { content_type: contentType, item_id: itemId });
}

/**
 * GA4 recommended `search`. The term is scrubbed by `redactSearchTerm`
 * (whitespace collapsed, e-mail addresses and phone-like numbers replaced,
 * length capped); an empty term sends nothing.
 */
export function trackSearch(term: string): void {
  const searchTerm = redactSearchTerm(term);
  if (!searchTerm) return;
  sendEvent('search', { search_term: searchTerm });
}

/** The Android "Get the app" bar: `app_banner_click` (Install) or `app_banner_dismiss`. */
export function trackAppBanner(action: 'click' | 'dismiss'): void {
  sendEvent(action === 'click' ? 'app_banner_click' : 'app_banner_dismiss');
}

/** One Web Vitals report, as the `web-vitals` library hands it over. */
export interface WebVitalReport {
  name: 'LCP' | 'INP' | 'CLS' | 'TTFB';
  value: number;
  delta: number;
  /** Unique per metric and page load: what GA4 groups a metric's reports by. */
  id: string;
  rating: 'good' | 'needs-improvement' | 'poor';
  navigationType: string;
}

/**
 * Real-user Web Vitals (web plan W5.2, see `@/lib/webVitals`): one event per
 * report, named after the metric (`LCP`, `INP`, `CLS`, `TTFB`) with the
 * parameters web.dev's GA4 guide uses — `value` is the delta, so a metric's
 * reports sum to its value; `metric_id` groups them — plus `metric_rating`,
 * `navigation_type` and `page_template` (home, list, brand, promotion, store,
 * sale, other). The template is the only page information added: never a
 * path, id, slug or token.
 */
export function trackWebVital(metric: WebVitalReport, pageTemplate: PageTemplate): void {
  sendEvent(metric.name, {
    value: metric.delta,
    metric_id: metric.id,
    metric_value: metric.value,
    metric_delta: metric.delta,
    metric_rating: metric.rating,
    navigation_type: metric.navigationType,
    page_template: pageTemplate,
  });
}
